// useEdgeSwipeBack — Gesto "arrastrar desde el borde izquierdo para volver".
//
// Replica el gesto nativo de iOS Safari (edge-swipe-back) que en el modo PWA
// standalone se pierde. Escucha touchstart / touchmove / touchend a nivel
// window y, si el gesto empieza dentro de los 20px del borde izquierdo, es
// mayormente horizontal, y supera 100px de arrastre horizontal, dispara
// `navigate(-1)`.
//
// Sólo se activa en dispositivos táctiles. En desktop es no-op.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const EDGE_START_PX = 20;         // Debe iniciar dentro de este margen izquierdo
const MIN_TRIGGER_PX = 100;       // Distancia mínima horizontal para disparar back
const MAX_VERTICAL_PX = 80;       // Si la vertical supera esto, cancelar (scroll)
const RATIO_MIN = 1.5;            // dx/dy mínimo para considerarse "horizontal"

const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export function useEdgeSwipeBack({ enabled = true } = {}) {
  const navigate = useNavigate();
  const [dragX, setDragX] = useState(0);
  // Refs para no re-crear listeners en cada render
  const activeRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });
  const dragXRef = useRef(0);

  useEffect(() => {
    if (!enabled || !isTouchDevice()) return undefined;

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      const t = e.touches[0];
      if (t.clientX <= EDGE_START_PX) {
        activeRef.current = true;
        startRef.current = { x: t.clientX, y: t.clientY };
        dragXRef.current = 0;
        setDragX(0);
      }
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || !e.touches || e.touches.length === 0) return;
      const t = e.touches[0];
      const dx = t.clientX - startRef.current.x;
      const dy = Math.abs(t.clientY - startRef.current.y);
      // Si el vertical domina, cancelar (probable scroll vertical).
      if (dy > MAX_VERTICAL_PX || (dx > 20 && dx / Math.max(1, dy) < RATIO_MIN)) {
        activeRef.current = false;
        dragXRef.current = 0;
        setDragX(0);
        return;
      }
      if (dx > 0) {
        dragXRef.current = dx;
        setDragX(dx);
      }
    };

    const onTouchEnd = () => {
      if (!activeRef.current) return;
      const finalDx = dragXRef.current;
      activeRef.current = false;
      dragXRef.current = 0;
      setDragX(0);
      if (finalDx >= MIN_TRIGGER_PX) {
        try { navigate(-1); } catch (err) { /* silencioso */ }
      }
    };

    const onTouchCancel = () => {
      activeRef.current = false;
      dragXRef.current = 0;
      setDragX(0);
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [enabled, navigate]);

  return { dragX };
}
